import React, { useState, useMemo, useEffect } from "react";
import { Icon } from "../kzIcons";

const STEPS = [
  "Problem",
  "Service",
  "Location",
  "Who",
  "When",
  "Photos",
  "Review",
];

const TITLES = [
  "What's the problem?",
  "What do you need?",
  "Where is the job?",
  "Who should come?",
  "When do you need us?",
  "Add photos",
  "Review your request",
];

const SUBS = [
  "Tell us what happened so we can send the right person.",
  "Pick the type of work.",
  "Add the full address so the craftsman can find you.",
  "Pick someone yourself, or let us find a suitable match.",
  "Choose a time, or ask for someone as soon as possible.",
  "Photos help the craftsman prepare. You can skip this step.",
  "Check the details before you send.",
];

const MAX_PHOTOS = 5;
const MAX_PHOTO_SIZE = 10 * 1024 * 1024;
const LAST = STEPS.length - 1;

const unsplash = (id) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=320&q=80`;

const FALLBACK_AVATAR = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(
    name || "Craftsman"
  )}&background=000000&color=fff000&size=96&bold=true`;

const DEFAULT_SERVICE_IMAGE = unsplash(
  "photo-1504307651254-35680f356dfd"
);

/*
 * Illustrative stock photographs for the service-selection screen.
 * These are not photographs of actual KaaKazini providers or jobs.
 * Backend-provided service images take precedence over these images.
 */
const SERVICE_IMAGES = [
  {
    keywords: ["plumb", "pipe", "tap", "water", "drain", "sink"],
    url: unsplash("photo-1607472586893-edb57bdc0e39"),
  },
  {
    keywords: ["electric", "wiring", "socket", "lighting"],
    url: unsplash("photo-1621905251189-08b45d6a269e"),
  },
  {
    keywords: ["carpent", "wood", "furniture", "joinery"],
    url: unsplash("photo-1504148455328-c376907d081c"),
  },
  {
    keywords: ["paint", "decorat"],
    url: unsplash("photo-1562259949-e8e7689d7828"),
  },
  {
    keywords: ["clean", "housekeeping"],
    url: unsplash("photo-1581578731548-c64695cc6952"),
  },
  {
    keywords: ["weld", "metal", "fabricat", "metalwork"],
    url: unsplash("photo-1504917595217-d4dc5ebe6122"),
  },
  {
    keywords: ["garden", "landscap", "lawn"],
    url: unsplash("photo-1416879595882-3373a0480b5b"),
  },
  {
    keywords: ["mason", "brick", "construction", "build"],
    url: unsplash("photo-1504307651254-35680f356dfd"),
  },
  {
    keywords: ["roof", "gutter"],
    url: unsplash("photo-1635424710928-7c8e8b3f4b27"),
  },
  {
    keywords: ["tile", "tiling", "floor"],
    url: unsplash("photo-1600607687939-ce8a6c25118c"),
  },
  {
    keywords: ["appliance", "technician", "electronics"],
    url: unsplash("photo-1581092160562-40aa08e78837"),
  },
  {
    keywords: ["tailor", "textile", "sewing", "fashion"],
    url: unsplash("photo-1590736969955-71cc94901144"),
  },
  {
    keywords: ["beauty", "hair", "barber", "salon"],
    url: unsplash("photo-1521590832167-7bcbfaa6381f"),
  },
];

const CSS = `
.rq-wrap {
  --ink: #000;
  --muted: #666;
  --fill: #f3f3f3;
  --fill-2: #e8e8e8;
  --line: #e5e5e5;
  --accent: #fff000;
  --bg: #fff;

  width: 100%;
  max-width: 620px;
  margin: 0 auto;
  color: var(--ink);
  background: var(--bg);
  font-family: Raleway, system-ui, sans-serif;
  font-size: 16px;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
}

.rq-wrap *,
.rq-wrap *::before,
.rq-wrap *::after {
  box-sizing: border-box;
}

.rq-wrap button,
.rq-wrap input,
.rq-wrap textarea,
.rq-wrap select {
  font: inherit;
}

.rq-wrap button:focus-visible,
.rq-wrap input:focus-visible,
.rq-wrap textarea:focus-visible,
.rq-wrap select:focus-visible,
.rq-wrap a:focus-visible {
  outline: 2px solid #000;
  outline-offset: 3px;
}

.rq-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.rq-top {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
}

.rq-back,
.rq-back-gap {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
}

.rq-back {
  border: 0;
  border-radius: 50%;
  background: var(--fill);
  color: var(--ink);
  cursor: pointer;
}

.rq-back:hover:not(:disabled) {
  background: var(--fill-2);
}

.rq-back:disabled {
  opacity: .45;
  cursor: not-allowed;
}

.rq-bar {
  flex: 1;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 4px;
}

.rq-bar i {
  height: 4px;
  border-radius: 4px;
  background: var(--fill-2);
}

.rq-bar i.done {
  background: var(--ink);
}

.rq-count {
  flex: 0 0 auto;
  color: var(--muted);
  font-size: 13px;
  white-space: nowrap;
}

.rq-h {
  margin: 24px 0 6px;
  font-size: clamp(27px, 5vw, 34px);
  line-height: 1.15;
  font-weight: 750;
  letter-spacing: -.035em;
}

.rq-sub {
  margin: 0 0 24px;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.55;
}

.rq-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
}

.rq-input,
.rq-textarea {
  display: block;
  width: 100%;
  min-width: 0;
  border: 2px solid transparent;
  border-radius: 9px;
  background: var(--fill);
  color: var(--ink);
}

.rq-input {
  height: 52px;
  padding: 0 14px;
}

.rq-textarea {
  min-height: 168px;
  padding: 14px;
  resize: vertical;
  line-height: 1.6;
}

.rq-input:focus,
.rq-textarea:focus {
  outline: none;
  border-color: var(--ink);
  background: #fff;
}

.rq-input::placeholder,
.rq-textarea::placeholder {
  color: #858585;
}

.rq-help {
  margin: 8px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.55;
}

.rq-field + .rq-field {
  margin-top: 20px;
}

.rq-money {
  position: relative;
}

.rq-money > span {
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 14px;
  font-weight: 700;
  pointer-events: none;
}

.rq-money .rq-input {
  padding-left: 55px;
}

.rq-where {
  display: grid;
  border: 2px solid transparent;
  border-radius: 9px;
  background: var(--fill);
}

.rq-where:focus-within {
  border-color: var(--ink);
  background: #fff;
}

.rq-where-row {
  display: grid;
  position: relative;
  grid-template-columns: 44px minmax(0, 1fr);
  align-items: center;
}

.rq-where-row + .rq-where-row {
  border-top: 1px solid var(--line);
}

.rq-where-row i {
  justify-self: center;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--ink);
  z-index: 1;
}

.rq-where-row i.sq {
  border-radius: 2px;
}

.rq-where-row::before {
  content: "";
  position: absolute;
  left: 21px;
  width: 2px;
  background: var(--ink);
}

.rq-where-row:first-child::before {
  top: 50%;
  bottom: 0;
}

.rq-where-row:last-child::before {
  top: 0;
  bottom: 50%;
}

.rq-where select,
.rq-where input {
  width: 100%;
  height: 52px;
  min-width: 0;
  padding: 0 14px 0 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--ink);
  font-size: 15px;
}

.rq-where select:focus,
.rq-where input:focus {
  outline: none;
}

.rq-where input::placeholder {
  color: #858585;
}

.rq-where select.empty {
  color: #858585;
}

.rq-list {
  margin: 0 -8px;
}

.rq-row {
  display: flex;
  align-items: center;
  gap: 13px;
  width: 100%;
  min-width: 0;
  padding: 13px 8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}

.rq-row:hover {
  background: #f8f8f8;
}

.rq-row.on {
  background: var(--fill);
}

.rq-thumb {
  display: block;
  width: 76px;
  height: 62px;
  flex: 0 0 76px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--fill-2);
}

.rq-avatar {
  display: block;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 50%;
  object-fit: cover;
  background: var(--fill-2);
}

.rq-ico {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 50%;
  background: var(--fill);
}

.rq-row.on .rq-ico {
  background: var(--ink);
  color: var(--accent);
}

.rq-body {
  flex: 1;
  min-width: 0;
}

.rq-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  font-size: 16px;
  font-weight: 750;
  overflow-wrap: anywhere;
}

.rq-meta {
  display: block;
  margin-top: 3px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.rq-radio {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  border: 2px solid #bcbcbc;
  border-radius: 50%;
  color: var(--accent);
}

.rq-row.on .rq-radio {
  border-color: var(--ink);
  background: var(--ink);
}

.rq-tag {
  padding: 2px 7px;
  border-radius: 4px;
  background: var(--ink);
  color: var(--accent);
  font-size: 10px;
  font-weight: 800;
}

.rq-rate {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 800;
}

.rq-group {
  margin: 22px 0 6px;
  font-size: 14px;
  font-weight: 800;
}

.rq-scroll {
  max-height: 380px;
  overflow-y: auto;
}

.rq-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 0 0;
  margin-top: 12px;
  border-top: 1px solid var(--line);
}

.rq-switch-row b {
  display: block;
  font-size: 14px;
  font-weight: 800;
}

.rq-switch-row span {
  display: block;
  margin-top: 3px;
  color: var(--muted);
  font-size: 13px;
}

.rq-switch {
  position: relative;
  width: 48px;
  height: 30px;
  flex: 0 0 48px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #bcbcbc;
  cursor: pointer;
}

.rq-switch::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fff;
  transition: transform .15s;
}

.rq-switch[aria-checked="true"] {
  background: var(--ink);
}

.rq-switch[aria-checked="true"]::after {
  transform: translateX(18px);
}

.rq-seg {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  background: var(--fill);
}

.rq-seg button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 46px;
  padding: 8px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--ink);
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
}

.rq-seg button.on {
  background: var(--ink);
  color: #fff;
}

.rq-when {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 20px;
}

.rq-when .rq-field + .rq-field {
  margin-top: 0;
}

.rq-note {
  margin-top: 16px;
  padding: 14px;
  border-radius: 9px;
  background: var(--fill);
  font-size: 14px;
  line-height: 1.55;
}

.rq-photos {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.rq-ph,
.rq-add {
  position: relative;
  aspect-ratio: 1;
  min-width: 0;
  overflow: hidden;
  border-radius: 9px;
  background: var(--fill);
}

.rq-ph img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rq-ph button {
  position: absolute;
  top: 6px;
  right: 6px;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background: var(--ink);
  color: #fff;
  cursor: pointer;
}

.rq-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px dashed #aaa;
  color: var(--ink);
  cursor: pointer;
  text-align: center;
}

.rq-add:hover {
  background: var(--fill-2);
}

.rq-add span {
  font-size: 12px;
  font-weight: 750;
}

.rq-file {
  display: none;
}

.rq-block {
  padding: 16px 0;
  border-top: 1px solid var(--line);
}

.rq-block:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.rq-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 800;
}

.rq-edit {
  padding: 7px 12px;
  border: 0;
  border-radius: 999px;
  background: var(--fill);
  color: var(--ink);
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.rq-edit:hover {
  background: var(--fill-2);
}

.rq-stops {
  display: grid;
  row-gap: 15px;
}

.rq-stop {
  display: grid;
  position: relative;
  grid-template-columns: 24px minmax(0, 1fr);
  align-items: start;
}

.rq-stop i {
  justify-self: center;
  width: 9px;
  height: 9px;
  margin-top: 6px;
  border-radius: 50%;
  background: var(--ink);
}

.rq-stop i.sq {
  border-radius: 2px;
}

.rq-stop:first-child::before {
  content: "";
  position: absolute;
  top: 14px;
  bottom: -20px;
  left: 11px;
  width: 2px;
  background: var(--ink);
}

.rq-stop b {
  display: block;
  font-size: 14px;
  overflow-wrap: anywhere;
}

.rq-stop span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.rq-svc {
  display: flex;
  align-items: center;
  gap: 13px;
}

.rq-kv {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 12px;
  padding: 9px 0;
  font-size: 14px;
}

.rq-kv span:first-child {
  color: var(--muted);
}

.rq-kv span:last-child {
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.rq-err {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 18px 0 0;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff0ee;
  color: #9f211a;
  font-size: 13px;
  line-height: 1.55;
  font-weight: 650;
}

.rq-err svg {
  flex: 0 0 auto;
  margin-top: 2px;
}

.rq-foot {
  position: sticky;
  bottom: 0;
  z-index: 5;
  margin-top: 24px;
  padding: 12px 0 16px;
  background: var(--bg);
}

.rq-go {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  padding: 12px 16px;
  border: 0;
  border-radius: 9px;
  background: var(--ink);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
}

.rq-go:hover:not(:disabled) {
  background: #252525;
}

.rq-go:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.rq-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: rq-spin .7s linear infinite;
}

@keyframes rq-spin {
  to { transform: rotate(360deg); }
}

.rq-map {
  position: relative;
  height: 220px;
  margin-top: 16px;
  overflow: hidden;
  border-radius: 14px;
  background: var(--fill);
}

.rq-map.sm {
  height: 140px;
}

.rq-map iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  filter: grayscale(1) contrast(1.05);
}

.rq-map-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: var(--muted);
  font-size: 13px;
  text-align: center;
}

.rq-credit {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 3px 8px;
  border-top-right-radius: 6px;
  background: rgba(255,255,255,.92);
  color: #444;
  font-size: 10px;
}

.rq-credit a {
  color: inherit;
}

.rq-sheet {
  position: relative;
}

.rq-grab {
  display: none;
}

@media (max-width: 600px) {
  .rq-map {
    height: 30vh;
    min-height: 190px;
  }

  .rq-map.sm {
    height: 20vh;
    min-height: 130px;
  }

  .rq-sheet.over {
    margin: -18px -4px 0;
    padding: 10px 4px 0;
    border-radius: 18px 18px 0 0;
    background: var(--bg);
  }

  .rq-sheet.over .rq-grab {
    display: block;
    width: 36px;
    height: 4px;
    margin: 0 auto 8px;
    border-radius: 4px;
    background: #d0d0d0;
  }

  .rq-sheet.over .rq-h {
    margin-top: 14px;
    font-size: 26px;
  }

  .rq-sheet.over .rq-sub {
    margin-bottom: 18px;
  }

  .rq-thumb {
    width: 66px;
    height: 56px;
    flex-basis: 66px;
  }
}

@media (max-width: 480px) {
  .rq-when {
    grid-template-columns: 1fr;
  }

  .rq-kv {
    grid-template-columns: 80px minmax(0, 1fr);
  }

  .rq-seg button {
    font-size: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rq-wrap *,
  .rq-wrap *::before,
  .rq-wrap *::after {
    animation: none !important;
    transition: none !important;
  }
}
`;

function Radio({ on }) {
  return (
    <span className="rq-radio" aria-hidden="true">
      {on && <Icon name="check" size={13} stroke={3} />}
    </span>
  );
}

function normalize(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function serviceName(service) {
  if (typeof service === "string") return service;

  if (!service || typeof service !== "object") return "";

  return (
    service.name ||
    service.title ||
    service.label ||
    service.service_name ||
    service.trade_name ||
    ""
  );
}

function serviceKey(service) {
  return service?.id ?? serviceName(service);
}

function locationName(location) {
  if (typeof location === "string") return location;

  if (!location || typeof location !== "object") return "";

  return (
    location.name ||
    location.label ||
    location.county ||
    location.location ||
    ""
  );
}

function nameOf(craftsman) {
  return (
    craftsman?.full_name ||
    craftsman?.name ||
    craftsman?.user?.full_name ||
    "Craftsman"
  );
}

function servicesOf(craftsman) {
  const values = [
    craftsman?.primary_service,
    craftsman?.service,
    craftsman?.trade,
    ...(Array.isArray(craftsman?.services)
      ? craftsman.services
      : []),
    ...(Array.isArray(craftsman?.specialisations)
      ? craftsman.specialisations
      : []),
    ...(Array.isArray(craftsman?.specializations)
      ? craftsman.specializations
      : []),
  ];

  return values
    .map((value) =>
      typeof value === "object" && value !== null
        ? serviceName(value)
        : String(value ?? "")
    )
    .map(normalize)
    .filter(Boolean);
}

function locationOf(craftsman) {
  const value =
    craftsman?.location ||
    craftsman?.county ||
    craftsman?.area ||
    craftsman?.profile?.location ||
    "";

  return normalize(
    typeof value === "object" ? locationName(value) : value
  );
}

function isUnavailable(craftsman) {
  return (
    craftsman?.is_available === false ||
    craftsman?.available === false ||
    craftsman?.availability === "unavailable" ||
    craftsman?.is_approved === false ||
    craftsman?.approved === false ||
    craftsman?.is_verified === false
  );
}

function localToday() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export default function RequestServiceTab({
  services = [],
  locations = [],
  craftsmen = [],
  mediaBase = "",
  preselected = null,
  busy = false,
  onSubmit,
}) {
  const [step, setStep] = useState(0);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(preselected ? "pick" : "auto");

  const [f, setF] = useState(() => ({
    description: "",
    service: preselected?.primary_service
      ? serviceName(preselected.primary_service)
      : "",
    location: normalize(
      typeof preselected?.location === "object"
        ? locationName(preselected.location)
        : preselected?.location
    ),
    address: "",
    date: "",
    time: "",
    budget: "",
    isUrgent: false,
    photos: [],
    craftsmanId: preselected?.id ?? null,
    fallback: true,
  }));

  const [geo, setGeo] = useState(null);
  const [geoStatus, setGeoStatus] = useState("idle");
  const [photoPreviews, setPhotoPreviews] = useState([]);

  const set = (key, value) => {
    setF((previous) => ({ ...previous, [key]: value }));
    setErr("");
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  /*
   * Create preview URLs when photos change, and release them
   * when the selection changes or this component unmounts.
   */
  useEffect(() => {
    const urls = f.photos.map((photo) => URL.createObjectURL(photo));

    setPhotoPreviews(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [f.photos]);

  const resolveImage = (url) => {
    if (!url || typeof url !== "string") return "";

    if (/^(https?:|data:|blob:)/i.test(url)) {
      return url;
    }

    const base = String(mediaBase || "").replace(/\/+$/, "");
    const path = url.startsWith("/") ? url : `/${url}`;

    return `${base}${path}`;
  };

  const imageOf = (service) => {
    const supplied =
      service && typeof service === "object"
        ? service.image_url ||
          service.imageUrl ||
          service.image ||
          service.photo ||
          service.icon_url ||
          service.thumbnail
        : "";

    if (supplied) return resolveImage(supplied);

    const label = normalize(serviceName(service));

    const match = SERVICE_IMAGES.find((entry) =>
      entry.keywords.some((keyword) => label.includes(keyword))
    );

    return match?.url || DEFAULT_SERVICE_IMAGE;
  };

  const photoOf = (craftsman) => {
    const supplied =
      craftsman?.profile_url ||
      craftsman?.profile_image_url ||
      craftsman?.profile_image ||
      craftsman?.profile_photo ||
      craftsman?.avatar ||
      craftsman?.photo ||
      craftsman?.user?.profile_image;

    return supplied
      ? resolveImage(supplied)
      : FALLBACK_AVATAR(nameOf(craftsman));
  };

  const isPreselected = (craftsman) =>
    preselected?.id != null &&
    String(craftsman?.id) === String(preselected.id);

  const options = useMemo(() => {
    const selectedService = normalize(f.service);
    const selectedLocation = normalize(f.location);

    if (!selectedService) return [];

    return craftsmen.filter((craftsman) => {
      if (!craftsman?.id || isUnavailable(craftsman)) return false;

      const matchingService = servicesOf(craftsman).some(
        (value) =>
          value === selectedService ||
          value.includes(selectedService) ||
          selectedService.includes(value)
      );

      const providerLocation = locationOf(craftsman);

      const matchingLocation =
        !selectedLocation ||
        !providerLocation ||
        providerLocation === selectedLocation;

      return (
        isPreselected(craftsman) ||
        (matchingService && matchingLocation)
      );
    });
  }, [craftsmen, f.service, f.location, preselected]);

  const chosen =
    craftsmen.find(
      (craftsman) =>
        String(craftsman?.id) === String(f.craftsmanId)
    ) ||
    (preselected?.id != null &&
    String(preselected.id) === String(f.craftsmanId)
      ? preselected
      : null);

  const selectedService = services.find(
    (service) =>
      normalize(serviceName(service)) === normalize(f.service)
  );

  const countyLabel = f.location
    ? f.location.charAt(0).toUpperCase() + f.location.slice(1)
    : "";

  const chooseCounty = (value) => {
    setF((previous) => {
      const selected = craftsmen.find(
        (craftsman) =>
          String(craftsman?.id) === String(previous.craftsmanId)
      );

      const providerLocation = selected
        ? locationOf(selected)
        : "";

      const removeSelection =
        selected &&
        !isPreselected(selected) &&
        value &&
        providerLocation &&
        providerLocation !== normalize(value);

      return {
        ...previous,
        location: value,
        craftsmanId: removeSelection
          ? null
          : previous.craftsmanId,
      };
    });

    setErr("");
  };

  /*
   * Map preview only. The map is illustrative and does not provide
   * live craftsman GPS, dispatch tracking, or a verified exact pin.
   */
  useEffect(() => {
    const address = f.address.trim();

    if (step < 2 || !f.location) {
      setGeo(null);
      setGeoStatus("idle");
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;

    setGeoStatus("loading");

    const county =
      f.location.charAt(0).toUpperCase() + f.location.slice(1);

    const lookup = async (query) => {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=ke&q=${encodeURIComponent(query)}`,
        {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        }
      );

      if (!response.ok) return null;

      const results = await response.json();
      const first = results?.[0];

      if (!first) return null;

      const lat = Number(first.lat);
      const lon = Number(first.lon);

      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return null;
      }

      return { lat, lon };
    };

    const timer = setTimeout(async () => {
      try {
        const exact =
          address.length >= 3
            ? await lookup(`${address}, ${county}, Kenya`)
            : null;

        if (cancelled) return;

        if (exact) {
          setGeo({ ...exact, precise: true });
          setGeoStatus("ok");
          return;
        }

        const area = await lookup(`${county} County, Kenya`);

        if (cancelled) return;

        setGeo(area ? { ...area, precise: false } : null);
        setGeoStatus(area ? "ok" : "fail");
      } catch (error) {
        if (
          !cancelled &&
          error?.name !== "AbortError"
        ) {
          setGeo(null);
          setGeoStatus("fail");
        }
      }
    }, 900);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [step, f.location, f.address]);

  const showMap = step === 2 || (step >= 3 && !!geo);

  const mapSrc = geo
    ? (() => {
        const delta = geo.precise ? 0.005 : 0.25;

        const bbox = [
          geo.lon - delta,
          geo.lat - delta,
          geo.lon + delta,
          geo.lat + delta,
        ].join("%2C");

        return (
          "https://www.openstreetmap.org/export/embed.html" +
          `?bbox=${bbox}&layer=mapnik&marker=${geo.lat}%2C${geo.lon}`
        );
      })()
    : "";

  const rules = [
    [
      () => f.description.trim().length >= 10,
      "Describe the problem in at least 10 characters.",
    ],
    [
      () => !!f.service,
      "Choose the service you need.",
    ],
    [
      () => !!f.location && !!f.address.trim(),
      "Choose a county and enter the job address.",
    ],
    [
      () => mode === "auto" || !!f.craftsmanId,
      'Pick a craftsman or choose "Best available".',
    ],
    [
      () => {
        if (f.isUrgent) return true;
        if (!f.date || !f.time) return false;

        const scheduled = new Date(`${f.date}T${f.time}:00`);

        return (
          !Number.isNaN(scheduled.getTime()) &&
          scheduled.getTime() > Date.now()
        );
      },
      "Choose a future date and time, or select As soon as possible.",
    ],
    [() => true, ""],
    [() => true, ""],
  ];

  const next = () => {
    const [valid, message] = rules[step];

    if (!valid()) {
      setErr(message);
      return;
    }

    setErr("");
    setStep((previous) => Math.min(previous + 1, LAST));
  };

  const goTo = (index) => {
    setErr("");
    setStep(Math.max(0, Math.min(index, LAST)));
  };

  const addPhotos = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const imageFiles = selectedFiles.filter((file) =>
      file.type.startsWith("image/")
    );

    if (imageFiles.length !== selectedFiles.length) {
      setErr("Only image files can be attached.");
    } else {
      setErr("");
    }

    const oversized = imageFiles.filter(
      (file) => file.size > MAX_PHOTO_SIZE
    );

    if (oversized.length) {
      setErr("Each photo must be 10 MB or smaller.");
    }

    const validFiles = imageFiles.filter(
      (file) => file.size <= MAX_PHOTO_SIZE
    );

    setF((previous) => {
      const remaining = Math.max(
        0,
        MAX_PHOTOS - previous.photos.length
      );

      if (validFiles.length > remaining) {
        setErr(`You can attach a maximum of ${MAX_PHOTOS} photos.`);
      }

      return {
        ...previous,
        photos: [
          ...previous.photos,
          ...validFiles.slice(0, remaining),
        ],
      };
    });

    // Allow the same file to be selected again after removal.
    event.target.value = "";
  };

  const removePhoto = (index) => {
    setF((previous) => ({
      ...previous,
      photos: previous.photos.filter((_, i) => i !== index),
    }));

    setErr("");
  };

  const formatDate = () => {
    if (f.isUrgent) return "As soon as possible";

    if (!f.date || !f.time) return "Not selected";

    const date = new Date(`${f.date}T${f.time}:00`);

    if (Number.isNaN(date.getTime())) {
      return `${f.date} at ${f.time}`;
    }

    return date.toLocaleString("en-KE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const submit = async () => {
    if (busy) return;

    if (typeof onSubmit !== "function") {
      setErr(
        "Request submission is not configured. Please contact support."
      );
      return;
    }

    for (let index = 0; index < rules.length; index += 1) {
      const [valid, message] = rules[index];

      if (!valid()) {
        setErr(message);
        setStep(index);
        return;
      }
    }

    if (
      f.budget !== "" &&
      (!Number.isFinite(Number(f.budget)) ||
        Number(f.budget) < 0)
    ) {
      setErr("Enter a valid, non-negative budget.");
      setStep(4);
      return;
    }

    if (f.photos.length > MAX_PHOTOS) {
      setErr(`You can attach a maximum of ${MAX_PHOTOS} photos.`);
      setStep(5);
      return;
    }

    if (mode === "pick" && !chosen) {
      setErr("Please select a listed craftsman, or choose Best available.");
      setStep(3);
      return;
    }

    try {
      setErr("");

      const formData = new FormData();

      /*
       * Preserve your existing backend field names.
       * Confirm these against the Django serializer before renaming.
       */
      formData.append("service", f.service);
      formData.append("description", f.description.trim());
      formData.append("location", f.location.toLowerCase());
      formData.append("address", f.address.trim());
      formData.append("isUrgent", String(f.isUrgent));

      if (f.budget !== "") {
        formData.append("budget", String(Number(f.budget)));
      }

      const scheduledAt = f.isUrgent
        ? new Date()
        : new Date(`${f.date}T${f.time}:00`);

      if (
        Number.isNaN(scheduledAt.getTime()) ||
        (!f.isUrgent && scheduledAt.getTime() <= Date.now())
      ) {
        setErr("Choose a valid date and time in the future.");
        setStep(4);
        return;
      }

      formData.append("schedule", scheduledAt.toISOString());

      if (mode === "pick" && f.craftsmanId != null) {
        formData.append(
          "preferred_craftsman",
          String(f.craftsmanId)
        );
        formData.append(
          "allow_fallback",
          String(f.fallback)
        );
      }

      f.photos.forEach((photo) => {
        formData.append("media", photo);
      });

      await onSubmit(formData);
    } catch (error) {
      setErr(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Could not send your request. Please try again."
      );
    }
  };

  return (
    <div className="rq-wrap">
      <style>{CSS}</style>

      <div className="rq-top">
        {step > 0 ? (
          <button
            type="button"
            className="rq-back"
            disabled={busy}
            aria-label="Go back"
            onClick={() => goTo(step - 1)}
          >
            <Icon name="arrowLeft" size={20} stroke={2.2} />
          </button>
        ) : (
          <span className="rq-back-gap" />
        )}

        <div
          className="rq-bar"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={STEPS.length}
          aria-valuenow={step + 1}
          aria-label={`Step ${step + 1} of ${STEPS.length}: ${STEPS[step]}`}
        >
          {STEPS.map((label, index) => (
            <i
              key={label}
              className={index <= step ? "done" : ""}
            />
          ))}
        </div>

        <span className="rq-count">
          {step + 1} of {STEPS.length}
        </span>
      </div>

      {showMap && (
        <div className={`rq-map${step === 2 ? "" : " sm"}`}>
          {geo ? (
            <>
              <iframe
                title="Approximate job location map"
                tabIndex={-1}
                loading="lazy"
                src={mapSrc}
              />

              <span className="rq-credit">
                <a
                  href="https://www.openstreetmap.org/copyright"
                  target="_blank"
                  rel="noreferrer"
                >
                  © OpenStreetMap contributors
                </a>
              </span>
            </>
          ) : (
            <div className="rq-map-empty">
              <Icon name="radar" size={22} />

              <span>
                {!f.location
                  ? "Choose a county to preview the area."
                  : geoStatus === "fail"
                  ? "Map unavailable. You can still continue."
                  : "Finding the approximate location..."}
              </span>
            </div>
          )}
        </div>
      )}

      <div className={`rq-sheet${showMap ? " over" : ""}`}>
        <span className="rq-grab" aria-hidden="true" />

        <h2 className="rq-h">{TITLES[step]}</h2>
        <p className="rq-sub">{SUBS[step]}</p>

        {/* Step 1: Describe the problem */}
        {step === 0 && (
          <div>
            <label className="rq-sr" htmlFor="rq-desc">
              Describe the problem
            </label>

            <textarea
              id="rq-desc"
              className="rq-textarea"
              value={f.description}
              maxLength={3000}
              placeholder="The kitchen sink is leaking under the cabinet and water is collecting on the floor."
              onChange={(event) =>
                set("description", event.target.value)
              }
            />

            <p className="rq-help">
              {f.description.length}/3000. Say when it started and
              how serious it is.
            </p>
          </div>
        )}

        {/* Step 2: Select a service */}
        {step === 1 &&
          (services.length === 0 ? (
            <div className="rq-note">
              No services are available right now. Try again later
              or contact support.
            </div>
          ) : (
            <div
              className="rq-list"
              role="radiogroup"
              aria-label="Service"
            >
              {services.map((service) => {
                const label = serviceName(service);
                const selected =
                  normalize(f.service) === normalize(label);

                return (
                  <button
                    key={serviceKey(service)}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`rq-row${selected ? " on" : ""}`}
                    onClick={() => {
                      const previousService = normalize(f.service);
                      const nextService = normalize(label);

                      setF((previous) => ({
                        ...previous,
                        service: label,
                        craftsmanId:
                          previousService === nextService
                            ? previous.craftsmanId
                            : null,
                      }));

                      setErr("");
                    }}
                  >
                    <img
                      className="rq-thumb"
                      src={imageOf(service)}
                      alt=""
                      loading="lazy"
                      onError={(event) => {
                        const image = event.currentTarget;

                        if (
                          image.dataset.fallback !== "true" &&
                          image.src !== DEFAULT_SERVICE_IMAGE
                        ) {
                          image.dataset.fallback = "true";
                          image.src = DEFAULT_SERVICE_IMAGE;
                        }
                      }}
                    />

                    <span className="rq-body">
                      <span className="rq-title">{label}</span>
                    </span>

                    <Radio on={selected} />
                  </button>
                );
              })}
            </div>
          ))}

        {/* Step 3: Location */}
        {step === 2 && (
          <>
            <div className="rq-where">
              <div className="rq-where-row">
                <i />

                <label className="rq-sr" htmlFor="rq-loc">
                  County
                </label>

                <select
                  id="rq-loc"
                  className={f.location ? "" : "empty"}
                  value={f.location}
                  onChange={(event) =>
                    chooseCounty(event.target.value)
                  }
                >
                  <option value="">Choose county</option>

                  {locations.map((location) => {
                    const label = locationName(location);
                    const value = normalize(label);

                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="rq-where-row">
                <i className="sq" />

                <label className="rq-sr" htmlFor="rq-addr">
                  Job address
                </label>

                <input
                  id="rq-addr"
                  value={f.address}
                  maxLength={500}
                  placeholder="Street, estate or landmark"
                  onChange={(event) =>
                    set("address", event.target.value)
                  }
                />
              </div>
            </div>

            <p className="rq-help" style={{ marginTop: 12 }}>
              Include the building, estate or a nearby landmark.
              A county alone is not enough.
            </p>
          </>
        )}

        {/* Step 4: Choose a craftsman or automatic matching */}
        {step === 3 && (
          <>
            <div
              className="rq-list"
              role="radiogroup"
              aria-label="Who should come"
            >
              <button
                type="button"
                role="radio"
                aria-checked={mode === "auto"}
                className={`rq-row${mode === "auto" ? " on" : ""}`}
                onClick={() => {
                  setMode("auto");
                  set("craftsmanId", null);
                }}
              >
                <span className="rq-ico">
                  <Icon name="radar" size={22} />
                </span>

                <span className="rq-body">
                  <span className="rq-title">
                    Best available
                  </span>

                  <span className="rq-meta">
                    Let KaaKazini match your request with a suitable
                    available craftsman.
                  </span>
                </span>

                <Radio on={mode === "auto"} />
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={mode === "pick"}
                className={`rq-row${mode === "pick" ? " on" : ""}`}
                onClick={() => {
                  setMode("pick");
                  setErr("");
                }}
              >
                <span className="rq-ico">
                  <Icon name="user" size={22} />
                </span>

                <span className="rq-body">
                  <span className="rq-title">
                    Choose a craftsman
                  </span>

                  <span className="rq-meta">
                    Select a listed provider yourself.
                  </span>
                </span>

                <Radio on={mode === "pick"} />
              </button>
            </div>

            {mode === "pick" &&
              (options.length === 0 ? (
                <div className="rq-note">
                  No matching craftsmen are listed for this
                  selection. Choose Best available to request
                  automatic matching.
                </div>
              ) : (
                <>
                  <p className="rq-group">
                    Matching craftsmen
                  </p>

                  <div
                    className="rq-list rq-scroll"
                    role="radiogroup"
                    aria-label="Matching craftsmen"
                  >
                    {options.map((craftsman) => {
                      const selected =
                        String(f.craftsmanId) ===
                        String(craftsman.id);

                      const rating = Number(
                        craftsman.average_rating ??
                          craftsman.rating ??
                          0
                      );

                      return (
                        <button
                          key={craftsman.id}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={`rq-row${selected ? " on" : ""}`}
                          onClick={() =>
                            set("craftsmanId", craftsman.id)
                          }
                        >
                          <img
                            className="rq-avatar"
                            src={photoOf(craftsman)}
                            alt=""
                            loading="lazy"
                            onError={(event) => {
                              const image = event.currentTarget;
                              image.onerror = null;
                              image.src = FALLBACK_AVATAR(
                                nameOf(craftsman)
                              );
                            }}
                          />

                          <span className="rq-body">
                            <span className="rq-title">
                              {nameOf(craftsman)}

                              {(craftsman.is_verified ||
                                craftsman.verified) && (
                                <span className="rq-tag">
                                  Verified
                                </span>
                              )}
                            </span>

                            <span className="rq-meta">
                              {craftsman.primary_service
                                ? serviceName(
                                    craftsman.primary_service
                                  )
                                : f.service}

                              {locationOf(craftsman)
                                ? ` · ${locationOf(craftsman)}`
                                : ""}
                            </span>
                          </span>

                          <span className="rq-rate">
                            {rating > 0
                              ? `★ ${rating.toFixed(1)}`
                              : "New"}
                          </span>

                          <Radio on={selected} />
                        </button>
                      );
                    })}
                  </div>

                  <div className="rq-switch-row">
                    <div>
                      <b id="rq-fb-label">
                        Allow another craftsman if busy
                      </b>

                      <span>
                        Let the system offer the job to another
                        suitable provider if needed.
                      </span>
                    </div>

                    <button
                      type="button"
                      role="switch"
                      aria-checked={f.fallback}
                      aria-labelledby="rq-fb-label"
                      className="rq-switch"
                      onClick={() =>
                        set("fallback", !f.fallback)
                      }
                    />
                  </div>
                </>
              ))}
          </>
        )}

        {/* Step 5: Schedule or request urgently */}
        {step === 4 && (
          <>
            <div className="rq-seg" role="group" aria-label="When">
              <button
                type="button"
                className={f.isUrgent ? "on" : ""}
                aria-pressed={f.isUrgent}
                onClick={() => {
                  setF((previous) => ({
                    ...previous,
                    isUrgent: true,
                    date: "",
                    time: "",
                  }));

                  setErr("");
                }}
              >
                <Icon name="clock" size={17} />
                As soon as possible
              </button>

              <button
                type="button"
                className={!f.isUrgent ? "on" : ""}
                aria-pressed={!f.isUrgent}
                onClick={() => {
                  setF((previous) => ({
                    ...previous,
                    isUrgent: false,
                  }));

                  setErr("");
                }}
              >
                <Icon name="calendar" size={17} />
                Schedule
              </button>
            </div>

            {!f.isUrgent && (
              <div className="rq-when">
                <div className="rq-field">
                  <label className="rq-label" htmlFor="rq-date">
                    Date
                  </label>

                  <input
                    id="rq-date"
                    type="date"
                    className="rq-input"
                    value={f.date}
                    min={localToday()}
                    onChange={(event) =>
                      set("date", event.target.value)
                    }
                  />
                </div>

                <div className="rq-field">
                  <label className="rq-label" htmlFor="rq-time">
                    Time
                  </label>

                  <input
                    id="rq-time"
                    type="time"
                    className="rq-input"
                    value={f.time}
                    onChange={(event) =>
                      set("time", event.target.value)
                    }
                  />
                </div>
              </div>
            )}

            <div className="rq-field" style={{ marginTop: 26 }}>
              <label className="rq-label" htmlFor="rq-budget">
                Budget (optional)
              </label>

              <div className="rq-money">
                <span>KSh</span>

                <input
                  id="rq-budget"
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  className="rq-input"
                  value={f.budget}
                  placeholder="Your budget"
                  onChange={(event) =>
                    set("budget", event.target.value)
                  }
                />
              </div>

              <p className="rq-help">
                This is your estimated budget, not a confirmed
                price. The craftsman may provide a quote.
              </p>
            </div>
          </>
        )}

        {/* Step 6: Attach photos */}
        {step === 5 && (
          <>
            <div className="rq-photos">
              {photoPreviews.map((url, index) => (
                <div className="rq-ph" key={url}>
                  <img
                    src={url}
                    alt={`Request attachment ${index + 1}`}
                  />

                  <button
                    type="button"
                    aria-label={`Remove photo ${index + 1}`}
                    onClick={() => removePhoto(index)}
                  >
                    <Icon name="x" size={14} stroke={2.6} />
                  </button>
                </div>
              ))}

              {f.photos.length < MAX_PHOTOS && (
                <label className="rq-add" htmlFor="rq-photos">
                  <Icon name="plus" size={22} />

                  <span>
                    {f.photos.length === 0
                      ? "Add photos"
                      : "Add more"}
                  </span>

                  <input
                    id="rq-photos"
                    type="file"
                    accept="image/*"
                    multiple
                    className="rq-file"
                    onChange={addPhotos}
                  />
                </label>
              )}
            </div>

            <p className="rq-help" style={{ marginTop: 12 }}>
              Up to {MAX_PHOTOS} images, each no larger than 10 MB.
              Avoid including private documents or sensitive details.
            </p>
          </>
        )}

        {/* Step 7: Review before submission */}
        {step === 6 && (
          <>
            <section className="rq-block">
              <div className="rq-block-head">
                <span>Location</span>

                <button
                  type="button"
                  className="rq-edit"
                  onClick={() => goTo(2)}
                >
                  Edit
                </button>
              </div>

              <div className="rq-stops">
                <div className="rq-stop">
                  <i />
                  <div>
                    <b>{countyLabel || "No county selected"}</b>
                    <span>County</span>
                  </div>
                </div>

                <div className="rq-stop">
                  <i className="sq" />
                  <div>
                    <b>{f.address || "No address entered"}</b>
                    <span>Job address</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="rq-block">
              <div className="rq-block-head">
                <span>Service and provider</span>

                <button
                  type="button"
                  className="rq-edit"
                  onClick={() => goTo(3)}
                >
                  Edit
                </button>
              </div>

              <div className="rq-svc">
                {mode === "pick" && chosen ? (
                  <img
                    className="rq-avatar"
                    src={photoOf(chosen)}
                    alt=""
                  />
                ) : (
                  <img
                    className="rq-thumb"
                    src={
                      selectedService
                        ? imageOf(selectedService)
                        : imageOf(f.service)
                    }
                    alt=""
                  />
                )}

                <div className="rq-body">
                  <div className="rq-title">
                    {f.service || "No service selected"}
                  </div>

                  <span className="rq-meta">
                    {mode === "pick" && chosen
                      ? `Preferred provider: ${nameOf(chosen)}`
                      : "KaaKazini will process your request for matching."}
                  </span>
                </div>
              </div>
            </section>

            <section className="rq-block">
              <div className="rq-block-head">
                <span>Request details</span>

                <button
                  type="button"
                  className="rq-edit"
                  onClick={() => goTo(0)}
                >
                  Edit
                </button>
              </div>

              {[
                ["Problem", f.description.trim()],
                ["When", formatDate()],
                [
                  "Budget",
                  f.budget !== ""
                    ? `KSh ${Number(f.budget).toLocaleString("en-KE")}`
                    : "Not set",
                ],
                [
                  "If busy",
                  mode === "pick"
                    ? f.fallback
                      ? "Allow another suitable provider"
                      : "Keep my preferred provider"
                    : "Automatic matching requested",
                ],
                [
                  "Photos",
                  f.photos.length
                    ? `${f.photos.length} attached`
                    : "None",
                ],
              ].map(([label, value]) => (
                <div className="rq-kv" key={label}>
                  <span>{label}</span>
                  <span>{value || "Not provided"}</span>
                </div>
              ))}
            </section>

            <p className="rq-help">
              Your request is not confirmed until the server accepts
              it. Final pricing, provider assignment and arrival
              details depend on the actual dispatch process.
            </p>
          </>
        )}

        {err && (
          <p className="rq-err" role="alert">
            <Icon name="alert" size={16} />
            <span>{err}</span>
          </p>
        )}

        <div className="rq-foot">
          {step < LAST ? (
            <button
              type="button"
              className="rq-go"
              onClick={next}
              disabled={busy}
            >
              {step === 5 && f.photos.length === 0
                ? "Skip photos"
                : "Continue"}
            </button>
          ) : (
            <button
              type="button"
              className="rq-go"
              onClick={submit}
              disabled={busy}
            >
              {busy ? (
                <>
                  <span className="rq-spinner" />
                  Sending request...
                </>
              ) : (
                "Send request"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
